<template>
  <el-input
    ref="input"
    v-bind="inputOption"
    :value="displayValue"
    :class="b()"
    v-on="listeners"
    @blur="handleBlur"
    @clear="clearNumber"
  >
    <slot
      slot="prepend"
      name="prepend"
    />
    <slot
      slot="prefix"
      name="prefix"
    />
    <slot
      slot="suffix"
      name="suffix"
    />
  </el-input>
</template>

<script>
import { excludeComponentProps, inheritComponentListener, wrapperComponentListener, getComponentProps } from '@zk/framework-vue';
import { InputNumber } from 'zk-ui';
import theme from '@zk/theme/lib/variables.scss';
import { isNil, isEqual } from 'lodash';
/* eslint-disable vue/no-side-effects-in-computed-properties */
// import Decimal from 'decimal.js';

const INT_REGEXP = /[^\-?\d]/g;
const REGEXP = /[^\-?\d.]/g;
const SAFE_NUM = 99999999999999;
const props = {
  blurCheckDot: {
    type: Boolean,
    default: false
  }
};

export default {
  name: 'NumberInput',
  inheritAttrs: false,
  props: {
    ...getComponentProps(InputNumber),
    ...props
  },
  data() {
    return {
      bemNS: theme.bemNS,
      displayValue: this.value
    };
  },
  computed: {
    isInteger() {
      return !this.precision || this.precision <= 0;
    },
    regexp() {
      return this.isInteger ? INT_REGEXP : REGEXP;
    },
    inputOption() {
      return {
        ...excludeComponentProps(this, props)
      };
    },
    listeners() {
      const usrEvent = inheritComponentListener(InputNumber, this);
      return {
        ...wrapperComponentListener(usrEvent, {
          blur: () => {
            if (this.blurCheckDot && this.displayValue && String(this.displayValue).endsWith('.')) {
              this.displayValue = this.displayValue.substring(0, this.displayValue.length - 1);
            }
          }
        }),
        input: v => {
          if (!v) {
            this.displayValue = null;
            return;
          }
          const nv = this.parseValue(v, this.displayValue);
          this.displayValue = nv;
          this.$emit('input-str', nv, v);
        }
      };
    },
    maxValue() {
      return isNil(this.max) || this.max === Infinity ? SAFE_NUM : this.max;
    },
    minValue() {
      return isNil(this.min) || this.min === -Infinity ? -SAFE_NUM : this.min;
    }
  },
  watch: {
    displayValue(value, oldValue) {
      if (value === 'NaN'){
        this.displayValue = oldValue;
        return;
      }
      if ((value !== oldValue && this.value !== value) || isEqual(value, -0)){
        const v = isEqual(value, '-') ? value : isNil(value) || value === '' ? null : this.isInteger ? parseInt(value) : Number(value);
        this.$emit('input', isNaN(v) ? isEqual(value, '-') ? v : null : v);
      }
    },
    value:{
      deep: true,
      handler(value, oldValue){
        if (value !== oldValue && value !== this.displayValue && value !== Number(this.displayValue)){
          this.displayValue = value;
        }
        if (isEqual(value, 0) && oldValue === null){
          this.displayValue = value;
        }
        if (isEqual(value, 0) && (oldValue === '-0' || oldValue === '-0.')){
          this.displayValue = value;
        }
        if (isEqual(value, '0') && (isEqual(oldValue, -0) || isEqual(oldValue, -0.))){
          this.displayValue = Number(value);
        }
      }
    }
  },
  methods: {
    parseValue(v) {
      if (!v || (v === '-' && this.minValue < 0)) {
        return v;
      }
      const nv = v.toString().replace(this.regexp, '');
      if (!nv || nv === '.') {
        return null;
      }
      const numberValue = this.getNumberValue(nv);
      
      if (this.isInteger) {
        return numberValue;
      }
      const str = this.getPrecisionStr(nv);
      // if (str.endsWith('.')) {
      //   return numberValue < this.maxValue && numberValue >= this.minValue ? str : old;
      // }
      return str;
    },
    getPrecisionStr(v) {
      if (!v.includes('.')) {
        return v === '-0' ? v : parseFloat(v).toString();
      }
      const [start, end = ''] = v.split('.');
      if (end.length <= this.precision) {
        return `${start}.${end}`;
      }
      return `${start}.${end.length > this.precision ? end.substring(0, this.precision) : end.padEnd(this.precision, '0')}`;
    },
    getNumberValue(v) {
      if (this.isInteger || !v.includes('.')) {
        return parseFloat(v);
      }
      return parseFloat(this.getPrecisionStr(v));
    },
    isRange(numberValue) {
      const { maxValue, minValue } = this;
      return numberValue <= maxValue && numberValue >= minValue;
    },
    focus() {
      this.$refs.input.focus();
    },
    handleBlur(){
      if (isNil(this.displayValue) || this.displayValue === '' || isEqual(this.displayValue, '-') || isEqual(this.displayValue, '-0') || isEqual(this.displayValue, '-0.')){
        this.$emit('blur-null');
      }
    },
    clearNumber(){
      this.$emit('focus-input');
    }
  }
};
</script>

<style lang="scss" scoped>

</style>
